/* eslint-disable max-nested-callbacks */
import React, { useEffect, useMemo } from 'react';
import { useQueries } from 'react-query';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';

import { getInitiativeChildren } from '@/features/canvas/api/initiative-relationships';
import { FilteredNodes, useEditorContext } from '@/features/canvas/contexts/editor-context';
import { components } from '@/types/api';

import { DependencyModeState } from '../../hooks/useDependencyMode';
import { useGanttIsolation } from '../../hooks/useGanttIsolation';
import { getInitiativeDates } from '../../utils/getInitiativeDates';
import { buildSectionTree, flattenTree } from '../../utils/treeUtils';
import { GanttDependencyArrows } from '../GanttDependencyArrows';
import { GanttParentChildGradients } from '../GanttParentChildGradients';

import { GanttTimelineSection } from './GanttTimelineSection';
import { TimelineBackgroundBands } from './TimelineBackgroundBands';

interface GanttTimelineProps {
  dependencyState: DependencyModeState;
  endDate: Dayjs;
  enhancedFilteredNodes: FilteredNodes[];
  expandedRows: Set<number>;
  getSectionHeight: (sectionId: number) => number;
  startDate: Dayjs;
  zoomMultiplier: number;
  allInitiativesRef?: React.MutableRefObject<FilteredNodes[]>;
  initiativesWithFields?: components['schemas']['Initiative'][];
}

// Helper function to apply filter colors to a node
const applyFilterColorsToNode = (
  node: Partial<FilteredNodes>,
  filters: components['schemas']['Filter'][],
  activeFilterIds: number[]
): FilteredNodes => {
  // Find active filters
  const activeFilters = filters.filter(f => f.id !== undefined && activeFilterIds.includes(f.id));

  // Check if this node has any filter assignments
  const filterWithNode = activeFilters.find(filter =>
    filter.nodes?.some(n => n.nodeId === node.id)
  );

  if (filterWithNode) {
    const filterNode = filterWithNode.nodes?.find(n => n.nodeId === node.id);
    if (filterNode) {
      // Find the option for this filter node
      const option = filterWithNode.options?.find(o => o.id === filterNode.optionId);
      if (option) {
        return {
          ...node,
          color: option.color || '',
          shape: (filterWithNode.shape as 'circle' | 'square' | 'triangle') || 'circle',
          size: option.size,
        } as FilteredNodes;
      }
    }
  }

  return {
    ...node,
    color: '',
    shape: 'circle',
  } as FilteredNodes;
};

const GanttTimeline: React.FC<GanttTimelineProps> = ({
  allInitiativesRef,
  startDate,
  endDate,
  enhancedFilteredNodes,
  expandedRows,
  dependencyState,
  getSectionHeight,
  zoomMultiplier,
  initiativesWithFields,
}) => {
  const {
    sortedVerticalSections,
    sortedHorizontalSections,
    roadmapNodes,
    filters,
    activeFilterIds,
    hiddenChildNodeIds,
  } = useEditorContext();

  // Combine sections exactly like SwimlaneView does
  const allSections = useMemo(
    () => [...sortedVerticalSections, ...sortedHorizontalSections],
    [sortedVerticalSections, sortedHorizontalSections]
  );

  // Use enhanced filtered nodes directly - no global re-adding of hidden children
  const allEnhancedNodes = enhancedFilteredNodes;

  // Get unique initiative IDs for fetching children relationships
  const initiativeIds = useMemo(() => {
    const ids = new Set<number>();
    roadmapNodes?.forEach(node => {
      if (node.initiatives?.id) {
        ids.add(node.initiatives.id);
      }
    });
    return Array.from(ids);
  }, [roadmapNodes]);

  // Fetch children for all initiatives
  const childrenQueries = useQueries(
    initiativeIds.map(id => ({
      queryKey: ['getInitiativeChildren', id],
      queryFn: () => getInitiativeChildren({ initiativeId: id }),
      enabled: true,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }))
  );

  // Check if queries are still loading
  const areQueriesLoading = childrenQueries.some(query => query.isLoading);

  // Create parent-child relationships map from query data
  const parentChildMap = useMemo(() => {
    // Don't build relationships until queries are done loading
    if (areQueriesLoading) {
      return new Map<number, components['schemas']['Initiative'][]>();
    }

    const map = new Map<number, components['schemas']['Initiative'][]>();
    childrenQueries.forEach((query, index) => {
      if (query.data && query.data.length > 0) {
        const parentId = initiativeIds[index]; // Use index to correlate with initiativeIds
        map.set(parentId, query.data);
      }
    });
    return map;
  }, [childrenQueries, initiativeIds, areQueriesLoading]);

  // Group initiatives by section using tree structure
  const initiativesBySection = useMemo(() => {
    const result: Record<number, { depth: number; node: FilteredNodes }[]> = {};

    allSections.forEach(section => {
      if (section.id !== undefined) {
        // Start with nodes that are naturally in this section
        const baseSectionNodes = allEnhancedNodes.filter(
          node => node.sectionId === section.id && node.initiatives
        );

        // Add hidden children that belong to parents in this section (multi-level)
        const enhancedSectionNodes = [...baseSectionNodes];
        const sectionNodeIds = new Set(baseSectionNodes.map(n => n.id));

        // Keep adding children until no more are found (handles multi-level nesting)
        let nodesToProcess = [...baseSectionNodes];
        const processedIds = new Set(baseSectionNodes.map(n => n.initiatives?.id).filter(Boolean));

        while (nodesToProcess.length > 0) {
          const newlyAdded: FilteredNodes[] = [];

          nodesToProcess.forEach(parentNode => {
            if (parentNode.initiatives?.id) {
              const childrenOfParent = parentChildMap.get(parentNode.initiatives.id) || [];

              childrenOfParent.forEach(childInitiative => {
                if (
                  childInitiative.id &&
                  !processedIds.has(childInitiative.id) &&
                  !sectionNodeIds.has(childInitiative.id)
                ) {
                  const childNodeInRoadmap = roadmapNodes?.find(
                    n => n.initiatives?.id === childInitiative.id
                  );

                  if (
                    childNodeInRoadmap &&
                    hiddenChildNodeIds.has(childNodeInRoadmap.id!) &&
                    childNodeInRoadmap.isVisible !== false
                  ) {
                    // Find the corresponding initiative with fields if available
                    const initiativeWithFields = initiativesWithFields?.find(
                      initiative => initiative.id === childNodeInRoadmap.initiatives?.id
                    );

                    // Create base enhanced child
                    const baseEnhancedChild = {
                      ...childNodeInRoadmap,
                      initiatives: initiativeWithFields || childNodeInRoadmap.initiatives,
                    };

                    // Apply filter colors if they exist for this node
                    const enhancedChild = applyFilterColorsToNode(
                      baseEnhancedChild,
                      filters || [],
                      activeFilterIds
                    );

                    const addedNode = {
                      ...enhancedChild,
                      color: enhancedChild.color || '',
                      shape: (enhancedChild.shape as 'circle' | 'square' | 'triangle') || 'circle',
                      size: enhancedChild.size || undefined,
                      isVisible: true,
                    };
                    enhancedSectionNodes.push(addedNode);
                    newlyAdded.push(addedNode);
                    sectionNodeIds.add(childNodeInRoadmap.id!);
                    processedIds.add(childInitiative.id);
                  }
                }
              });
            }
          });

          nodesToProcess = newlyAdded; // Process newly added nodes for THEIR children
        }

        // Build tree structure using enhanced section nodes
        const tree = buildSectionTree(enhancedSectionNodes, parentChildMap);

        // Flatten the tree based on expanded state
        const flattened = flattenTree(tree, expandedRows);

        // Create a map of child to parent for date inheritance
        const childToParentMap = new Map<number, FilteredNodes>();
        tree.forEach(root => {
          const mapParentChild = (parent: (typeof tree)[0]) => {
            if (parent.children) {
              parent.children.forEach(child => {
                if (child.node?.initiatives?.id && parent.node?.initiatives?.id) {
                  childToParentMap.set(child.node.initiatives.id, parent.node);
                }
                mapParentChild(child);
              });
            }
          };
          mapParentChild(root);
        });

        // Convert flattened tree items to FilteredNodes with date inheritance, preserving depth
        const sectionInitiatives = flattened.map(item => {
          const { node, depth } = item;

          let processedNode = node;

          // Check if this node needs date inheritance
          if (node.initiatives?.id && childToParentMap.has(node.initiatives.id)) {
            const parent = childToParentMap.get(node.initiatives.id)!;
            const { startDate: childStartDate, endDate: childEndDate } = getInitiativeDates(
              node.initiatives
            );
            const { startDate: parentStartDate, endDate: parentEndDate } = getInitiativeDates(
              parent.initiatives
            );

            // Check if we need to inherit any dates
            const needsStartDate = !childStartDate && parentStartDate;
            const needsEndDate = !childEndDate && parentEndDate;

            if (needsStartDate || needsEndDate) {
              // Create a new node with inherited dates
              const updatedFields = [...(node.initiatives.fields || [])];

              // Helper function to update date fields
              const updateDateField = (fieldTitle: string, dateValue: string) => {
                const fieldIndex = updatedFields.findIndex(f => f.title === fieldTitle);
                if (fieldIndex >= 0) {
                  updatedFields[fieldIndex] = {
                    ...updatedFields[fieldIndex],
                    value: dateValue,
                  };
                } else {
                  updatedFields.push({ type: 'date', title: fieldTitle, value: dateValue });
                }
              };

              if (needsStartDate) {
                updateDateField('Start Date', parentStartDate);
              }
              if (needsEndDate) {
                updateDateField('End Date', parentEndDate);
              }

              processedNode = {
                ...node,
                initiatives: {
                  ...node.initiatives,
                  fields: updatedFields,
                },
              };
            }
          }

          return { node: processedNode, depth };
        });

        result[section.id] = sectionInitiatives;
      }
    });

    return result;
  }, [
    allEnhancedNodes,
    allSections,
    parentChildMap,
    expandedRows,
    roadmapNodes,
    hiddenChildNodeIds,
    initiativesWithFields,
    filters,
    activeFilterIds,
  ]);

  // Create node counts based on tree structure
  const sectionNodeCounts = useMemo(() => {
    const result: Record<number, number> = {};
    Object.entries(initiativesBySection).forEach(([sectionId, initiativesWithDepth]) => {
      result[Number(sectionId)] = initiativesWithDepth.length;
    });
    return result;
  }, [initiativesBySection]);

  // Create a flat sorted list for dependency arrows using tree structure
  const sortedInitiativesForDependencies = useMemo(() => {
    const flatList: FilteredNodes[] = [];
    Object.values(initiativesBySection).forEach(sectionInitiatives => {
      flatList.push(...sectionInitiatives.map(item => item.node));
    });
    return flatList;
  }, [initiativesBySection]);

  if (allInitiativesRef) {
    allInitiativesRef.current = sortedInitiativesForDependencies;
  }

  // Gantt-specific isolation - separate from Sunray dependency isolation
  const { isolationState, isolateInitiative, clearIsolation, isIsolationActive } =
    useGanttIsolation(sortedInitiativesForDependencies);

  // Clear isolation when dependency mode is turned off
  useEffect(() => {
    if (!dependencyState.isDependencyMode) {
      clearIsolation();
    }
  }, [dependencyState.isDependencyMode, clearIsolation]);

  // ESC key handler to clear isolation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isIsolationActive) {
        clearIsolation();
      }
    };

    // Only add listener when dependency mode is ON and isolation is active
    if (dependencyState.isDependencyMode && isIsolationActive) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dependencyState.isDependencyMode, isIsolationActive, clearIsolation]);

  return (
    <TimelineContent>
      <TimelineBackgroundBands startDate={startDate} endDate={endDate} />
      <TodayLine startDate={startDate} endDate={endDate} />
      <TimelineGap />
      {allSections.map((section, index) => {
        if (section.id === undefined) return null;

        const sectionInitiativesWithDepth = initiativesBySection[section.id] || [];
        const nodeCount = sectionNodeCounts[section.id] || 0;
        const isLastSection = index === allSections.length - 1;

        return (
          <GanttTimelineSection
            key={section.id}
            allInitiatives={sortedInitiativesForDependencies}
            initiatives={sectionInitiativesWithDepth.map(item => item.node)}
            initiativesWithDepth={sectionInitiativesWithDepth}
            timelineStart={startDate}
            timelineEnd={endDate}
            nodeCount={nodeCount}
            isLastSection={isLastSection}
            getSectionHeight={getSectionHeight}
            sectionId={section.id}
            isDependencyMode={dependencyState.isDependencyMode}
            onBarClick={isolateInitiative}
            isolatedInitiativeId={isolationState.isolatedInitiativeId}
            connectedInitiativeIds={isolationState.connectedInitiativeIds}
            isIsolationActive={isIsolationActive}
          />
        );
      })}

      {/* Render parent-child gradients */}
      <GanttParentChildGradients
        initiatives={(() => {
          // Create a flattened list of all initiatives with cumulative positions
          const flatList: { cumulativeY: number; depth: number; node: FilteredNodes }[] = [];
          let cumulativeY = 0;

          allSections.forEach(section => {
            if (section.id === undefined) return;

            const sectionInitiativesWithDepth = initiativesBySection[section.id] || [];

            sectionInitiativesWithDepth.forEach(item => {
              flatList.push({
                depth: item.depth,
                node: item.node,
                cumulativeY,
              });

              // Add the row height for this initiative
              const rowHeight = item.depth > 0 ? 20 : 28; // GANTT_CHILD_HEIGHT : GANTT_PARENT_HEIGHT
              cumulativeY += rowHeight;
            });

            // Add section padding/spacing if needed
            const nodeCount = sectionNodeCounts[section.id] || 0;
            const calculatedHeight =
              nodeCount > 0
                ? sectionInitiativesWithDepth.reduce(
                    (sum, item) => sum + (item.depth > 0 ? 20 : 28),
                    0
                  ) + 6 // GANTT_SECTION_PADDING
                : 97.06;
            const sidebarHeight = getSectionHeight(section.id);
            const finalSectionHeight = Math.max(calculatedHeight, sidebarHeight, 97.06);

            // If section extends beyond calculated height, add that space
            if (finalSectionHeight > calculatedHeight) {
              cumulativeY += finalSectionHeight - calculatedHeight;
            }
          });

          return flatList;
        })()}
        timelineStart={startDate}
        timelineEnd={endDate}
        expandedRows={expandedRows}
        parentChildMap={parentChildMap}
      />

      {/* Render arrows at the timeline level to handle cross-section dependencies */}
      <GanttDependencyArrows
        dependencyState={dependencyState}
        timelineStart={startDate}
        timelineEnd={endDate}
        getSectionHeight={getSectionHeight}
        zoomMultiplier={zoomMultiplier}
        initiativesBySection={initiativesBySection}
        connectedConnectionIds={isolationState.connectedConnectionIds}
        isIsolationActive={isIsolationActive}
        enhancedInitiatives={sortedInitiativesForDependencies}
      />
    </TimelineContent>
  );
};

const TimelineContent = styled.div`
  position: relative;
  width: 100%;
  min-height: 100%;

  /* Clean content area with no background patterns */
  background: ${({ theme }) => theme.palette.brand.white};
  background-image: none;
  background-size: auto;
  background-repeat: no-repeat;

  /* Specifically target potential line sources */
  border: none;
  outline: none;
  box-shadow: none;
`;

const TodayLine: React.FC<{ endDate: Dayjs; startDate: Dayjs }> = ({ startDate, endDate }) => {
  const today = dayjs();
  const totalDays = endDate.diff(startDate, 'days');
  const daysSinceStart = today.diff(startDate, 'days');

  // Only show the line if today is within the timeline range
  if (today.isBefore(startDate) || today.isAfter(endDate)) {
    return null;
  }

  const leftPercent = (daysSinceStart / totalDays) * 100;
  const formattedDate = today.format('MMMM D, YYYY');

  return <TodayLineStyled left={leftPercent} title={formattedDate} />;
};

const TimelineGap = styled.div`
  height: 0px;
  width: 100%;
  background-color: transparent;
  border-bottom: none;
`;

const TodayLineStyled = styled.div<{ left: number }>`
  position: absolute;
  left: ${({ left }) => left}%;
  top: 0; /* Start from top of component */
  bottom: 0;
  width: 2px;
  background-color: ${({ theme }) => theme.palette.brand.primary};
  z-index: 20;
  pointer-events: auto;
`;

export { GanttTimeline };
